import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { Clock, FileText, MessageSquare, Globe, Landmark, UserCheck, Repeat } from "lucide-react";
import { DeleteAccountModal } from "../DeleteAccountModal";
import { formatPhoneInput } from "../../utils/phoneUtils";
import { API } from "../../App";

const BUSINESS_TYPES = [
  { value: "locksmith", label: "Locksmith", icon: "🔐" },
  { value: "garage_door", label: "Garage Door", icon: "🚪" },
  { value: "carpet_cleaning", label: "Carpet Cleaning", icon: "🧹" },
  { value: "hvac", label: "HVAC", icon: "❄️" },
  { value: "plumbing", label: "Plumbing", icon: "🔧" },
  { value: "electrical", label: "Electrical", icon: "⚡" },
  { value: "cleaning", label: "Cleaning", icon: "🧽" },
  { value: "chimney", label: "Chimney", icon: "🧱" },
  { value: "custom", label: "Custom / Other", icon: "⚙️" },
];

const TIMEZONES = [
  { value: "America/New_York", label: "Eastern Time (ET)", offset: "UTC-5/UTC-4" },
  { value: "America/Chicago", label: "Central Time (CT)", offset: "UTC-6/UTC-5" },
  { value: "America/Denver", label: "Mountain Time (MT)", offset: "UTC-7/UTC-6" },
  { value: "America/Phoenix", label: "Arizona Time (AZ)", offset: "UTC-7" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)", offset: "UTC-8/UTC-7" },
  { value: "America/Anchorage", label: "Alaska Time (AK)", offset: "UTC-9/UTC-8" },
  { value: "Pacific/Honolulu", label: "Hawaii Time (HI)", offset: "UTC-10" },
  { value: "America/Puerto_Rico", label: "Atlantic Time (AT)", offset: "UTC-4" },
  { value: "UTC", label: "UTC (Coordinated Universal Time)", offset: "UTC" },
  { value: "Europe/London", label: "London (GMT/BST)", offset: "UTC+0/UTC+1" },
  { value: "Europe/Paris", label: "Central European Time", offset: "UTC+1/UTC+2" },
  { value: "Asia/Dubai", label: "Dubai (GST)", offset: "UTC+4" },
  { value: "Asia/Singapore", label: "Singapore (SGT)", offset: "UTC+8" },
  { value: "Asia/Tokyo", label: "Japan (JST)", offset: "UTC+9" },
  { value: "Australia/Sydney", label: "Sydney (AEST)", offset: "UTC+10/UTC+11" },
];

const BusinessSettingsTab = ({
  businessSettings, setBusinessSettings, saveBusinessSettings,
  showDeleteModal, setShowDeleteModal,
}) => {
  // Phone numbers owned by this account — populates the Primary SMS Number
  // dropdown. Same source the Messages page uses for its from-number picker.
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [phoneNumbersLoading, setPhoneNumbersLoading] = useState(true);
  // Stripe Tax activation status (US automatic tax runs on the Stripe account;
  // "pending" = head-office address missing in Stripe Dashboard → Settings → Tax)
  const [stripeTaxStatus, setStripeTaxStatus] = useState(null);

  const chargeTaxUS = businessSettings.charge_tax === true && businessSettings.business_country !== "CA";
  useEffect(() => {
    if (!chargeTaxUS) { setStripeTaxStatus(null); return; }
    const token = localStorage.getItem("token");
    axios
      .get(`${API}/tax/stripe-status`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setStripeTaxStatus(r.data))
      .catch(() => setStripeTaxStatus(null));
  }, [chargeTaxUS]);

  useEffect(() => {
    let active = true;
    const token = localStorage.getItem("token");
    axios
      .get(`${API}/settings/phone-numbers`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (active) setPhoneNumbers(Array.isArray(r.data) ? r.data : (r.data?.numbers || [])); })
      .catch(() => { if (active) setPhoneNumbers([]); })
      .finally(() => { if (active) setPhoneNumbersLoading(false); });
    return () => { active = false; };
  }, []);

  return (
    <Card className="glass-card border-theme" data-testid="business-settings-card">
      <CardHeader className="p-4 sm:p-6">
        <CardTitle className="text-foreground font-['Manrope'] text-base sm:text-lg">Business Information</CardTitle>
        <CardDescription className="text-muted-foreground text-xs sm:text-sm">
          Configure your company details and business type
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 sm:space-y-6 p-4 sm:p-6 pt-0">
        <div className="space-y-2 sm:space-y-3">
          <Label className="text-muted-foreground text-sm">Business Type</Label>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
            {BUSINESS_TYPES.map((type) => (
              <button
                key={type.value}
                onClick={() => setBusinessSettings({ ...businessSettings, business_type: type.value })}
                className={`p-3 sm:p-4 rounded-xl border transition-all text-left ${
                  businessSettings.business_type === type.value
                    ? "bg-[#FFCC00]/20 border-[#FFCC00]/50 text-[#FFCC00]"
                    : "bg-theme-surface border-theme text-muted-foreground hover:border-white/20"
                }`}
                data-testid={`business-type-${type.value}`}
              >
                <span className="text-xl sm:text-2xl mb-1 sm:mb-2 block">{type.icon}</span>
                <span className="text-xs sm:text-sm font-medium">{type.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <div className="space-y-1.5 sm:space-y-2">
            <Label className="text-muted-foreground text-sm">Business Name</Label>
            <Input value={businessSettings.business_name} onChange={(e) => setBusinessSettings({ ...businessSettings, business_name: e.target.value })} className="input-glass" placeholder="Your Company Name" data-testid="business-name-input" />
          </div>
          <div className="space-y-1.5 sm:space-y-2">
            <Label className="text-muted-foreground text-sm">Business Phone</Label>
            <Input value={businessSettings.business_phone} onChange={(e) => setBusinessSettings({ ...businessSettings, business_phone: formatPhoneInput(e.target.value) })} className="input-glass" placeholder="(555) 123-4567" data-testid="business-phone-input" />
          </div>
        </div>

        <div className="space-y-1.5 sm:space-y-2">
          <Label className="text-muted-foreground text-sm">Business Address</Label>
          <Input value={businessSettings.business_address} onChange={(e) => setBusinessSettings({ ...businessSettings, business_address: e.target.value })} className="input-glass" placeholder="123 Main St, City, State ZIP" data-testid="business-address-input" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <div className="space-y-1.5 sm:space-y-2">
            <Label className="text-muted-foreground text-sm">Tax ID / EIN</Label>
            <Input value={businessSettings.tax_id} onChange={(e) => setBusinessSettings({ ...businessSettings, tax_id: e.target.value })} className="input-glass" placeholder="XX-XXXXXXX" data-testid="business-tax-input" />
          </div>
          <div className="space-y-1.5 sm:space-y-2">
            <Label className="text-muted-foreground text-sm">License Number</Label>
            <Input value={businessSettings.license_number} onChange={(e) => setBusinessSettings({ ...businessSettings, license_number: e.target.value })} className="input-glass" placeholder="License #" data-testid="business-license-input" />
          </div>
        </div>

        <div className="space-y-1.5 sm:space-y-2">
          <Label className="text-muted-foreground text-sm flex items-center gap-2">
            <Globe className="w-4 h-4" /> Business Country
          </Label>
          <p className="text-xs text-muted-foreground mb-2">Address autocomplete will suggest addresses from this country on every address form</p>
          <Select value={businessSettings.business_country || "US"} onValueChange={(value) => setBusinessSettings({ ...businessSettings, business_country: value })}>
            <SelectTrigger className="input-glass" data-testid="business-country-select"><SelectValue placeholder="Select country" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="US" data-testid="business-country-us">United States</SelectItem>
              <SelectItem value="CA" data-testid="business-country-ca">Canada</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2 rounded-xl border border-border bg-theme-surface p-3.5" data-testid="tax-settings-section">
          <div className="flex items-center justify-between gap-3">
            <div>
              <Label className="text-foreground text-sm flex items-center gap-2">
                <Landmark className="w-4 h-4" /> Does this business charge tax?
              </Label>
              <p className="text-xs text-muted-foreground mt-1">
                When enabled, Fieldz AI automatically determines the correct tax rate from each job's service address
                ({businessSettings.business_country === "CA" ? "Canadian GST/HST/PST/QST rules" : "US state/county/city/district rates"})
              </p>
            </div>
            <Switch checked={businessSettings.charge_tax === true}
              onCheckedChange={(v) => setBusinessSettings({ ...businessSettings, charge_tax: v })}
              data-testid="charge-tax-switch" />
          </div>
          {businessSettings.charge_tax === true && (
            <div className="pt-2 border-t border-border/60 space-y-2" data-testid="tax-settings-details">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                <span>Tax Country: <span className="text-foreground font-medium">{businessSettings.business_country === "CA" ? "Canada" : "United States"}</span></span>
                <span className="inline-flex items-center gap-1 text-teal-600 dark:text-teal-400 font-medium">Automatic Tax Calculation: Enabled</span>
              </div>
              {chargeTaxUS && stripeTaxStatus && !stripeTaxStatus.active && (
                <div className="rounded-lg border border-sky-500/40 bg-sky-500/10 px-3 py-2.5" data-testid="stripe-tax-status-warning">
                  <p className="text-xs font-semibold text-sky-600 dark:text-sky-400">US tax works out of the box — Stripe Tax is an optional precision upgrade</p>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Jobs and invoices automatically use each state's official average combined rate (state + average local, Tax Foundation July 2025).
                    Activating Stripe Tax upgrades this to exact street-level city/county/district rates.
                  </p>
                  <p className="text-[11px] mt-1.5">
                    <a href="https://dashboard.stripe.com/settings/tax" target="_blank" rel="noreferrer"
                       className="text-sky-600 dark:text-sky-400 font-medium underline underline-offset-2"
                       data-testid="stripe-tax-dashboard-link">
                      Optional: activate Stripe Tax in the Stripe Dashboard
                    </a>
                    {" "}— Fieldz AI switches to exact rates automatically, no other changes needed.
                  </p>
                </div>
              )}
              {chargeTaxUS && stripeTaxStatus?.active && (
                <p className="text-[11px] text-teal-600 dark:text-teal-400" data-testid="stripe-tax-status-active">
                  Stripe Tax is active — exact street-level US rates resolve automatically from each job's service address.
                </p>
              )}
              <div className="flex items-center justify-between gap-3">
                <div>
                  <Label className="text-muted-foreground text-xs">Allow manual tax override</Label>
                  <p className="text-[11px] text-muted-foreground">Owners/admins can override the automatic tax on a job (with a recorded reason)</p>
                </div>
                <Switch checked={businessSettings.tax_manual_override_allowed !== false}
                  onCheckedChange={(v) => setBusinessSettings({ ...businessSettings, tax_manual_override_allowed: v })}
                  data-testid="tax-override-allowed-switch" />
              </div>
              <p className="text-[11px] text-muted-foreground">
                Multi-location: each location can override "charge tax" in Settings → Locations. The tax rate itself always follows the job's service address.
              </p>
            </div>
          )}
        </div>

        {/* Technician job approval */}
        <div className="rounded-xl border border-border/60 bg-muted/20 p-3 sm:p-4 space-y-3" data-testid="job-approval-settings-card">
          <div className="flex items-center justify-between gap-3">
            <div>
              <Label className="text-foreground text-sm flex items-center gap-2">
                <UserCheck className="w-4 h-4" /> Require technician job approval
              </Label>
              <p className="text-xs text-muted-foreground mt-1">
                When ON, assigned technicians must Accept or Reject each job (via SMS link or their portal).
                When OFF, assigned jobs are automatically accepted — technicians are still notified, but no approval is needed and the Accept/Reject options are hidden.
              </p>
            </div>
            <Switch checked={businessSettings.require_job_approval !== false}
              onCheckedChange={(v) => setBusinessSettings({ ...businessSettings, require_job_approval: v })}
              data-testid="require-job-approval-switch" />
          </div>
        </div>

        {/* Recurring jobs */}
        <div className="rounded-xl border border-border/60 bg-muted/20 p-3 sm:p-4 space-y-3" data-testid="recurring-jobs-settings-card">
          <div className="flex items-center justify-between gap-3">
            <div>
              <Label className="text-foreground text-sm flex items-center gap-2">
                <Repeat className="w-4 h-4" /> Enable recurring jobs
              </Label>
              <p className="text-xs text-muted-foreground mt-1">
                Adds a "Recurring job" option to the job form. When a job is marked recurring, the next
                one is created automatically for the same customer — same details, no technician, status
                "New Lead" — at the interval you choose (every N days, weeks, or months).
              </p>
            </div>
            <Switch checked={businessSettings.recurring_jobs_enabled === true}
              onCheckedChange={(v) => setBusinessSettings({ ...businessSettings, recurring_jobs_enabled: v })}
              data-testid="recurring-jobs-enabled-switch" />
          </div>
        </div>

        <div className="space-y-1.5 sm:space-y-2">
          <Label className="text-muted-foreground text-sm flex items-center gap-2">
            <Clock className="w-4 h-4" /> Business Timezone
          </Label>
          <p className="text-xs text-muted-foreground mb-2">Set your business timezone for accurate date/time display across all reports and filters</p>
          <Select value={businessSettings.timezone || "America/New_York"} onValueChange={(value) => setBusinessSettings({ ...businessSettings, timezone: value })}>
            <SelectTrigger className="input-glass" data-testid="timezone-select"><SelectValue placeholder="Select timezone" /></SelectTrigger>
            <SelectContent className="max-h-60">
              {TIMEZONES.map((tz) => (
                <SelectItem key={tz.value} value={tz.value}>{tz.label} — {tz.offset}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5 sm:space-y-2">
          <Label className="text-muted-foreground text-sm flex items-center gap-2">
            <Clock className="w-4 h-4" /> Time Format
          </Label>
          <p className="text-xs text-muted-foreground mb-2">How times appear in customer notifications — 12-hour (1:00 PM) or 24-hour (13:00). Store locations can override this per location.</p>
          <Select value={businessSettings.time_format || "12h"} onValueChange={(value) => setBusinessSettings({ ...businessSettings, time_format: value })}>
            <SelectTrigger className="input-glass" data-testid="time-format-select"><SelectValue placeholder="Select time format" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="12h">12-hour (1:00 PM)</SelectItem>
              <SelectItem value="24h">24-hour (13:00)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5 sm:space-y-2">
          <Label className="text-muted-foreground text-sm flex items-center gap-2">
            <Clock className="w-4 h-4" /> Default Jobs View
          </Label>
          <p className="text-xs text-muted-foreground mb-2">Which view the Jobs page opens in by default. Store locations can override this per location.</p>
          <Select value={businessSettings.default_jobs_view || "calendar"} onValueChange={(value) => setBusinessSettings({ ...businessSettings, default_jobs_view: value })}>
            <SelectTrigger className="input-glass" data-testid="default-jobs-view-select"><SelectValue placeholder="Select default view" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="calendar">Calendar</SelectItem>
              <SelectItem value="timeline">Timeline</SelectItem>
              <SelectItem value="list">List</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5 sm:space-y-2">
          <Label className="text-muted-foreground text-sm flex items-center gap-2">
            <MessageSquare className="w-4 h-4" /> Primary SMS Number
          </Label>
          <p className="text-xs text-muted-foreground mb-2">
            Default "from" number for SMS sends. The Messages page may still let you pick
            another owned number per conversation. If unset, the system falls back to whatever
            number the conversation/UI selects.
          </p>
          {phoneNumbersLoading ? (
            <p className="text-xs text-muted-foreground">Loading numbers…</p>
          ) : phoneNumbers.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">
              No phone numbers attached to this account yet. Add one in Settings → Phone Numbers first.
            </p>
          ) : (
            <Select
              value={businessSettings.primary_sms_number || "__none__"}
              onValueChange={(value) => setBusinessSettings({
                ...businessSettings,
                primary_sms_number: value === "__none__" ? "" : value,
              })}
            >
              <SelectTrigger className="input-glass" data-testid="primary-sms-number-select">
                <SelectValue placeholder="Auto (use conversation default)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__" data-testid="primary-sms-number-opt-none">
                  Auto (use conversation default)
                </SelectItem>
                {phoneNumbers.map((n) => (
                  <SelectItem
                    key={n.phone_number}
                    value={n.phone_number}
                    data-testid={`primary-sms-number-opt-${n.phone_number}`}
                  >
                    {n.friendly_name ? `${n.friendly_name} — ${n.phone_number}` : n.phone_number}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <Button onClick={saveBusinessSettings} className="btn-primary w-full sm:w-auto" data-testid="save-business-btn">Save Business Settings</Button>

        {/* PDF Export Options */}
        <div className="pt-4 border-t border-theme">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="w-4 h-4 text-muted-foreground" />
            <Label className="text-foreground font-semibold text-sm">PDF Export Options</Label>
          </div>
          <div className="flex items-center justify-between bg-theme-surface rounded-lg p-4 border border-theme">
            <div>
              <Label className="text-foreground text-sm">Hide company profit in exported PDFs</Label>
              <p className="text-xs text-muted-foreground mt-0.5">When enabled, technician and job source PDF reports will not include the profit column</p>
            </div>
            <Switch
              checked={businessSettings.hide_profit_in_pdf || false}
              onCheckedChange={(checked) => setBusinessSettings({ ...businessSettings, hide_profit_in_pdf: checked })}
              data-testid="hide-profit-pdf-switch"
            />
          </div>

          <div className="flex items-center justify-between bg-theme-surface rounded-lg p-4 border border-theme mt-3">
            <div>
              <Label className="text-foreground text-sm">Show product photos on PDFs</Label>
              <p className="text-xs text-muted-foreground mt-0.5">Embed a small photo next to each inventory line item on invoices and estimates</p>
            </div>
            <Switch
              checked={businessSettings.show_product_photos_on_pdf !== false}
              onCheckedChange={(checked) => setBusinessSettings({ ...businessSettings, show_product_photos_on_pdf: checked })}
              data-testid="show-photos-pdf-switch"
            />
          </div>
        </div>

        {/* Report Email Template */}
        <div className="pt-4 border-t border-theme">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="w-4 h-4 text-muted-foreground" />
            <Label className="text-foreground font-semibold text-sm">Report Email Template</Label>
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            Customize the email sent when you email a technician's PDF report from
            Financials → Technician tab. Placeholders:
            {" "}
            <code className="text-[10px] text-amber-500">{"{tech_name}"}</code>,{" "}
            <code className="text-[10px] text-amber-500">{"{period_label}"}</code>,{" "}
            <code className="text-[10px] text-amber-500">{"{company_name}"}</code>.
            Leave blank to use the default.
          </p>
          <div className="space-y-3">
            <div>
              <Label className="text-foreground text-sm">Subject</Label>
              <Input
                value={(businessSettings.report_email_template || {}).subject || ""}
                onChange={(e) => setBusinessSettings({
                  ...businessSettings,
                  report_email_template: {
                    ...(businessSettings.report_email_template || {}),
                    subject: e.target.value,
                  },
                })}
                placeholder="Your performance report from {company_name} — {period_label}"
                className="bg-theme-surface border-theme"
                data-testid="report-email-template-subject"
              />
            </div>
            <div>
              <Label className="text-foreground text-sm">Message (HTML allowed)</Label>
              <textarea
                value={(businessSettings.report_email_template || {}).body_html || ""}
                onChange={(e) => setBusinessSettings({
                  ...businessSettings,
                  report_email_template: {
                    ...(businessSettings.report_email_template || {}),
                    body_html: e.target.value,
                  },
                })}
                rows={6}
                placeholder={"<p>Hi {tech_name},</p>\n<p>Please find your performance report for {period_label} attached.</p>"}
                className="w-full rounded-md border border-theme bg-theme-surface px-3 py-2 text-sm text-foreground font-mono placeholder:text-muted-foreground/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                data-testid="report-email-template-body"
              />
              <p className="text-[10px] text-muted-foreground/60 mt-1">
                Sent as HTML. Plain text works too — line breaks render as expected.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-red-500/20">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-red-500/5 rounded-xl p-4 border border-red-500/20">
            <div>
              <h3 className="text-red-400 font-semibold text-sm">Delete Account</h3>
              <p className="text-muted-foreground text-xs mt-1">Permanently delete your account and all associated data. This action cannot be undone.</p>
            </div>
            <Button variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300 whitespace-nowrap text-sm" onClick={() => setShowDeleteModal(true)} data-testid="delete-account-btn">
              Request Account Deletion
            </Button>
          </div>
        </div>
        <DeleteAccountModal open={showDeleteModal} onOpenChange={setShowDeleteModal} />
      </CardContent>
    </Card>
  );
};

export default BusinessSettingsTab;
